import { ChangeEvent, useEffect, useState, useCallback } from "react"
import { DropDownForm } from "src/components/common/form/DropdownForm"
import { InputForm } from "src/components/common/form/input/InputForm"
import { defaultMST4030_R01HeaderDto, MST4030_R01HeaderDto } from "./Dto_MST4030_R01"
import { useCommonCodeStore } from "src/store/useCommonCodeStore"
import { useMST4030_R01Store } from "./Store_MST4030_R01"
import { BRDiv } from "src/components/common/common"
import { SingleDateForm } from "src/components/common/form/SingleDateForm"
import { formatDate } from "src/utils/string/stringUtil"

const normalizeInspectType = (value?: string | null) => (value ?? '').toString().trim().toUpperCase()
const inboundCodes = ['IN']
const outboundCodes = ['OUT']

// Props 타입 정의: data는 선택된 설비 정보(선택적)
interface Props {
	data?: MST4030_R01HeaderDto
	defaultMST4030_R01HeaderRow: MST4030_R01HeaderDto
}

// TOOLING 설비 정보 입력/수정 컴포넌트
export const Info_MST4030_R01 = ({ data, defaultMST4030_R01HeaderRow }: Props) => {

	// zustand 스토어에서 상태 및 setter 가져오기
	const { 
		editMST4030_R01,
		setEditMST4030_R01,
		MST4030_R01DetailList,
		deletedMST4030_R01DetailList,
	} = useMST4030_R01Store();
	const { 
		eqmTypeList,
		eqmStatusList,
		compCdList,
		orgCdList,
		inspectionStatusList
	} = useCommonCodeStore();

	// 보유량 계산 헬퍼 함수
	const getHoldQtyEffect = useCallback((inspectType?: string | null) => {
		const normalized = normalizeInspectType(inspectType)
		if (inboundCodes.includes(normalized)) return 1
		if (outboundCodes.includes(normalized)) return -1
		return 0
	}, [])

	const [manufactureDtPickerVisible, setManufactureDtPickerVisible] = useState(false);
	const [manufactureDtPickerPos, setManufactureDtPickerPos] = useState<{top: number, left: number, height: number}>({top: 0, left: 0, height: 0});
	const [disposalDtPickerVisible, setDisposalDtPickerVisible] = useState(false);
	const [disposalDtPickerPos, setDisposalDtPickerPos] = useState<{top: number, left: number, height: number}>({top: 0, left: 0, height: 0});

	// 보유량 자동 계산 (Detail의 변경내역에 따라)
	useEffect(() => {
		if (!editMST4030_R01) return;
		if (inspectionStatusList.length === 0) return; // 공통코드가 로드되지 않았으면 계산하지 않음

		// 보유량 계산 헬퍼 함수
		const calculateHoldQtyImpact = (detailList: typeof MST4030_R01DetailList, excludeDeleted: boolean = false) => {
			let impact = 0;
			detailList.forEach((detail) => {
				// 삭제된 행은 계산에서 제외
				if (excludeDeleted && deletedMST4030_R01DetailList.includes(detail.valueIndex?.toString() || '')) {
					return;
				}

				const transportQty = detail.transportQty || 0;
				const effect = getHoldQtyEffect(detail.inspectType);

				if (!effect) return;

				impact += effect * transportQty;
			});
			return impact;
		};

		// 현재 디테일 리스트의 영향을 계산 (삭제 행 제외)
		let calculatedHoldQty = calculateHoldQtyImpact(MST4030_R01DetailList, true);

		// 보유량이 음수가 되지 않도록 보정 (필요시)
		calculatedHoldQty = Math.max(0, calculatedHoldQty);

		// 보유량이 변경된 경우에만 업데이트 (무한 루프 방지)
		if (editMST4030_R01.holdQty !== calculatedHoldQty) {
			setEditMST4030_R01({
				...editMST4030_R01,
				holdQty: calculatedHoldQty
			});
		}
	}, [MST4030_R01DetailList, deletedMST4030_R01DetailList, setEditMST4030_R01, getHoldQtyEffect])

	// input/select 값 변경 핸들러
	const handleChange = (e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;

		setEditMST4030_R01({
			...editMST4030_R01,
			[name]: value
		});
	}

	const inputWrapperStyle = {
		wrapperStyle: "w-[250px] min-w-[250px] mr-[10px] mb-[5px]",
		titleStyle: "w-[100px] min-w-[100px]",
		inputStyle: "w-[150px] min-w-[150px]",
	}

	// 렌더링
	return(
		<div className="w-full h-full overflow-y-auto flex border border-gray-300">
			<div className={`
				w-[60%]
				h-full flex flex-wrap content-start p-[12px]`}>

				<InputForm 
					title="설비코드" 
					value={editMST4030_R01.equipmentCd} 
					editable={true} 
					name="equipmentCd" 
					onChange={handleChange} 
					wrapperOption={inputWrapperStyle} 
				/>
				<InputForm 
					title="설비명" 
					value={editMST4030_R01.equipmentNm} 
					editable={true} 
					name="equipmentNm" 
					onChange={handleChange} 
					wrapperOption={inputWrapperStyle} 
				/>
				<DropDownForm 
					title="설비유형"
					value={editMST4030_R01.equipmentType}
					data={eqmTypeList}
                    editable={false}
                    name="equipmentType"
					initLabelLess={true}
					onChange={handleChange}
					wrapperOption={inputWrapperStyle}
				/>
				<DropDownForm 
					title="사업장"
					value={editMST4030_R01.compCd}
					data={compCdList}
					editable={true}
					name="compCd"
					initLabelLess={true}
					onChange={handleChange}
					wrapperOption={inputWrapperStyle}
				/>
				<DropDownForm 
					title="관리부서"
					value={editMST4030_R01.eqmOrgcd}
					data={orgCdList}
					editable={true}
					name="eqmOrgcd"
					initLabelLess={true}
					onChange={handleChange}
					wrapperOption={inputWrapperStyle}
				/>
				<InputForm title="제조사" value={editMST4030_R01.manufacturerNm} editable={true} name="manufacturerNm" onChange={handleChange} wrapperOption={inputWrapperStyle} />
				
				<SingleDateForm
					title="제조일자"
					value={editMST4030_R01?.manufactureDt ? new Date(editMST4030_R01.manufactureDt) : new Date()}
					setValue={(value: Date | undefined) => setEditMST4030_R01({...defaultMST4030_R01HeaderRow, ...editMST4030_R01, manufactureDt: value ? formatDate(value) : undefined})}
					editable={true}
					visible={manufactureDtPickerVisible}
					setVisible={setManufactureDtPickerVisible}
					onChange={handleChange}
					pickerPos={manufactureDtPickerPos}
					setPickerPos={setManufactureDtPickerPos}
					wrapperOption={inputWrapperStyle}
				/>
				<InputForm title="일련번호" value={editMST4030_R01.eqmSerial} editable={true} name="eqmSerial" onChange={handleChange} wrapperOption={inputWrapperStyle} />

				<InputForm title="SIZE(W)" value={editMST4030_R01.sizeWidth} editable={true} name="sizeWidth" onChange={handleChange} wrapperOption={inputWrapperStyle} />
				<InputForm title="SIZE(H)" value={editMST4030_R01.sizeHeight} editable={true} name="sizeHeight" onChange={handleChange} wrapperOption={inputWrapperStyle} />
				<InputForm title="SIZE(D)" value={editMST4030_R01.sizeDepth} editable={true} name="sizeDepth" onChange={handleChange} wrapperOption={inputWrapperStyle} />
				<InputForm title="무게(KG)" value={editMST4030_R01.sizeWeight} editable={true} name="sizeWeight" onChange={handleChange} wrapperOption={inputWrapperStyle} />

				<InputForm 
					title="보유량" 
					value={editMST4030_R01.holdQty || 0} 
					editable={false} 
					name="holdQty" 
					onChange={handleChange} 
					wrapperOption={inputWrapperStyle} 
				/>

				<DropDownForm 
					title="상태"
					value={editMST4030_R01.eqmStatus}
					data={eqmStatusList}
					editable={true}
					name="eqmStatus"
					initLabelLess={true}
					onChange={handleChange}
					wrapperOption={inputWrapperStyle}
				/>
				<SingleDateForm
					title="폐기일자"
					value={editMST4030_R01?.disposalDt ? new Date(editMST4030_R01.disposalDt) : undefined}
					setValue={(value: Date | undefined) => setEditMST4030_R01({...defaultMST4030_R01HeaderRow, ...editMST4030_R01, disposalDt: value ? formatDate(value) : undefined})}
					editable={true}
					visible={disposalDtPickerVisible}
					setVisible={setDisposalDtPickerVisible}
					onChange={handleChange}
					pickerPos={disposalDtPickerPos}
					setPickerPos={setDisposalDtPickerPos}
					wrapperOption={inputWrapperStyle}
					placeholder="-"
				/>

				<BRDiv />

				<InputForm title="비고" value={editMST4030_R01.remark} editable={true} wrapperOption={{
					wrapperStyle: "w-[670px] min-w-[670px] mr-[10px] mb-[10px]",
					titleStyle: "w-[100px] min-w-[100px]",
					inputStyle: "w-full min-w-full"
				}} name="remark" onChange={handleChange} />
			</div>
			{/* <div className="w-[40%] h-full flex flex-wrap gap-[12px] pt-[12px]">
				<ImageForm title="설비사진" image={editMST4030_R01.eqmImage} onImageChange={(eqmImage) => setEditMST4030_R01({ ...editMST4030_R01, eqmImage })} wrapperOption={eqmImageWrapperStyle} />
				<ImageForm title="설비사진2" image={editMST4030_R01.eqmImage2} onImageChange={(eqmImage2) => setEditMST4030_R01({ ...editMST4030_R01, eqmImage2 })} wrapperOption={eqmImageWrapperStyle} />
			</div> */}
		</div>
	)
}

